import React, { useCallback, useContext, useMemo } from 'react';
import { Flex, List, useScreenSize } from '@banhala/ui-kit';
import { ScrollContext } from '../../../../../context/ScrollContext';
import { useGoodsDetailAtomics } from '../queries/useGoodsDetailAtomics';
import { isNil } from 'shared/src/utils';
import { useGoodsSno } from '../../../../../hooks/useGoodsSno';
import {
  useIsSectionVisible,
  useMountWhenNearViewport,
} from '../../../../../store/sectionVisibility';
import { AtomicComponent } from 'shared/src/component/AtomicRenderer/utils/createAtomicComponent';
import { useLikeMap, useOutfitBookmarkMap } from 'shared/src/hooks';
import {
  retainedState,
  useRetainedState,
} from 'shared/src/hooks/retainedState';
import { AtomicScreenProvider } from 'shared/src/component/AtomicRenderer/context/AtomicScreenContext';
import { useGoodsInstanceId } from '../../../../../hooks/useGoodsInstanceId';
import { useSameMarketRecommendOnly } from '../../../../../hooks/useSameMarketRecommendOnly';
import { isSameMarketProxyComponent } from '../../../../../utils/isSameMarketProxyComponent';

// atomic 컴포넌트별 측정 높이를 visit 단위(retained)로 보관한다. 지연 mount placeholder 가
// 첫 방문 뒤엔 실제 높이로 안정화되어(remount·재진입에도 보존) 스크롤 점프를 막는다.
const atomicItemHeightsSlot = retainedState<Record<number, number>>(() => ({}));
// 첫 방문(측정 전) placeholder 추정 높이. 대략 상품 3열 그리드 한두 줄 규모.
const ESTIMATED_ATOMIC_ITEM_HEIGHT = 600;

/**
 * atomic 컴포넌트 하나를 "뷰포트 근처에 올 때만" mount 한다(지연 mount).
 * mount 전엔 추정/보관된 높이의 placeholder 로 자리만 잡아, 정보탭 mount 시 모든 추천/연관
 * 섹션의 카드가 한 commit 에 통째로 렌더되던 fiber 폭증(수백~수천 fiber, 긴 commit)을 없앤다.
 * 화면에 실제로 보이는 컴포넌트만 렌더 → JS 스레드 스파이크 감소.
 */
const LazyAtomicItem = (props: {
  instanceKey: string;
  index: number;
  children: React.ReactNode;
}) => {
  const { instanceKey, index, children } = props;
  const [mountRef, mounted] = useMountWhenNearViewport(
    `${instanceKey}/atomic-item-${index}`,
  );
  const [heights, setHeights] = useRetainedState(atomicItemHeightsSlot);
  const estimatedHeight = heights[index] ?? ESTIMATED_ATOMIC_ITEM_HEIGHT;

  const handleLayout = useCallback(
    ({ nativeEvent }: { nativeEvent: { layout: { height: number } } }) => {
      const h = nativeEvent.layout.height;
      setHeights(prev => (prev[index] === h ? prev : { ...prev, [index]: h }));
    },
    [index, setHeights],
  );

  return (
    <Flex ref={mountRef} height={mounted ? undefined : estimatedHeight}>
      {mounted ? <Flex onLayout={handleLayout}>{children}</Flex> : null}
    </Flex>
  );
};

const ProxySkeleton = () => {
  const { width: screenWidth } = useScreenSize();

  const cardWidth = useMemo(() => {
    return (screenWidth - 2) / 3;
  }, [screenWidth]);

  return (
    <Flex>
      <Flex.Row
        padding={{ horizontal: 16, vertical: 16 }}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Flex
          backgroundColor={'bg_tertiary'}
          width={98}
          height={24}
          borderRadius={4}
        ></Flex>
        <Flex
          backgroundColor={'bg_tertiary'}
          width={64}
          height={18}
          borderRadius={4}
        ></Flex>
      </Flex.Row>
      <Flex>
        <List<number>
          data={Array.from({ length: 9 }, (_, index) => index)}
          numColumns={3}
          columnWrapperStyle={{ gap: 2 }}
          renderItem={({ item }) => {
            return (
              <Flex flex={1} position={'relative'} key={item}>
                <Flex
                  width={cardWidth}
                  height={cardWidth * 1.2}
                  backgroundColor={'bg_tertiary'}
                />
                <Flex margin={{ top: 4, bottom: 20 }} gap={2}>
                  <Flex
                    width={cardWidth * 0.8}
                    height={20}
                    backgroundColor={'bg_tertiary'}
                  />
                  <Flex
                    width={cardWidth * 0.9}
                    height={16}
                    backgroundColor={'bg_tertiary'}
                  />
                  <Flex
                    width={cardWidth * 0.4}
                    height={14}
                    margin={{ top: 4 }}
                    backgroundColor={'bg_tertiary'}
                  />
                </Flex>
              </Flex>
            );
          }}
        />
      </Flex>
    </Flex>
  );
};

export const GoodsAtomicRenderer = React.memo<{
  focused: boolean;
  screen: string;
  onChangeHeight: (height: number) => void;
}>(props => {
  const { onChangeHeight } = props;
  const key = useGoodsInstanceId('atomic');
  const sno = useGoodsSno();

  const isVisible = useIsSectionVisible(key);
  const { isScrolling } = useContext(ScrollContext);
  const { likeMap, setLikeMap } = useLikeMap({});
  const { bookmarkMap, setBookmarkMap } = useOutfitBookmarkMap({});
  const extraParams = useMemo(() => ({ goods_sno: String(sno) }), [sno]);

  const { data: atomic } = useGoodsDetailAtomics(isVisible);
  const sameMarketRecommendOnly = useSameMarketRecommendOnly();
  // 기획전 이벤트 연관상품 off 실험: 해당 마켓 상품(176) proxy 만 남긴다.
  const components = useMemo(() => {
    const all = atomic?.components ?? [];
    if (!sameMarketRecommendOnly) return all;
    return all.filter(isSameMarketProxyComponent);
  }, [atomic, sameMarketRecommendOnly]);

  // const ref = useRef<ListRef<IScreensComponent>>(null);

  const handleLayout = useCallback(
    ({ nativeEvent }: { nativeEvent: { layout: { height: number } } }) => {
      onChangeHeight(nativeEvent.layout.height);
    },
    [onChangeHeight],
  );

  if (isNil(atomic)) {
    if (!props.focused) return null;

    return <ProxySkeleton />;
  }

  return (
    <Flex onLayout={handleLayout}>
      <AtomicScreenProvider value={'GOODS_DETAIL'}>
        {components.map((component, index) => (
          <LazyAtomicItem
            key={`Goods-Atomic-${index}`}
            instanceKey={key}
            index={index}
          >
            <AtomicComponent
              component={component}
              screen={'GOODS_DETAIL'}
              index={index}
              likeMap={likeMap}
              setLikeMap={setLikeMap}
              bookmarkMap={bookmarkMap}
              setBookmarkMap={setBookmarkMap}
              extraParams={extraParams}
              parentIsScrolling={isScrolling}
              isVisible={isVisible}
            />
          </LazyAtomicItem>
        ))}
      </AtomicScreenProvider>
    </Flex>
  );
});
